import React, { useState, useEffect } from "react";
import "./TabViewer.scss";
import { deleteExpense, getExpensesByCourtRef, saveExpense, updateExpense } from '../../services/api/ExpenseApi.js';
import { deleteNote, getNotesByCourtRef, saveNote, updateNote } from '../../services/api/NoteApi.js';
import { deleteTask, getTasksByCourtRef, saveTask, updateTask } from '../../services/api/TaskApi.js';
import { getLawyers } from '../../services/api/LawyersApi.js';
import { RenderOrders } from "./RenderOrders";
import { RenderTimeline } from "./RenderTimeline";
import { RenderDocuments } from "./RenderDocuments";
import { RenderNotes } from "./RenderNotes";
import { RenderExpenses } from "./RenderExpenses";
import { RenderTasks } from "./RenderTasks";
import { NoteForm } from "../../forms/NoteForm/NoteForm.js";
import { TaskForm } from "../../forms/TaskForm/TaskForm.js";
import { getAddedDocs, getDirectoryTree, getMyDocs } from "../../services/api/AddDocApi.js";
import { RenderAdditionalDocs } from "./RenderAdditionalDocs.js";
import { useBubbleSettings } from "../../context/BubbleSettingsContext.js";
import { useAuth } from "../../context/AuthContext.js";
import { RenderMyDocs } from "./RenderMyDocs.js";
import { ExpenseForm } from "../../forms/ExpenseForm/ExpenseForm.js";
import { deleteLabel } from "../../services/api/LabelsApi.js";
import { getLabelByCourtRef } from "../../services/api/LabelApi.js";
import { LabelForm } from "../../forms/LabelForm/LabelForm.js";
import { RenderLabels } from "./RenderLabels.js";
import { RenderObjectionDetails } from "./RenderObjectionDetails.js";
import { getAllGlobalSettingsData } from "../../services/api/GlobalSettingsApi.js";
import { RenderCustomColumns } from "./RenderCustomColumns.js";

const TabViewer = ({ caseData, selectedCourtType, refreshCaseDetailLabels, selectedCaseType }) => {
  const { role } = useAuth()
  const [activeTab, setActiveTab] = useState("Orders");
  const [expensesInfo, setExpensesInfo] = useState([]);
  const [caseSettings, setCaseSettings] = useState(null);
  const [notesInfo, setNotesInfo] = useState([]);
  const [tasksInfo, setTasksInfo] = useState([]);
  const [labelsInfo, setLabelsInfo] = useState([]);
  const [AdditionalDocsInfo, setAdditionalDocsInfo] = useState([]);
  const [MyDocInfo, setMyDocInfo] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState("");
  const [formData, setFormData] = useState({});
  const [taskTypeTab, setTaskTypeTab] = useState("OneTime");
  const orderInfo = caseData?.case?.orderInfo || caseData?.case?.orders || [];
  const isSupremeCourtSource = (caseData?.source?.toLowerCase() || '').includes('supreme') || selectedCourtType === 'SupremeCourt';
  const scListingDates = isSupremeCourtSource && Array.isArray(caseData?.case?.listingDates?.listings)
    ? caseData.case.listingDates.listings.map(l => ({
        hearingDate: l.date || '',
        stage: l.stage || '',
        judge: l.bench || '',
        purposeOfHearing: l.purpose || '',
        causeListType: l.miscRegular || '',
        businessOnDate: '',
      }))
    : [];
  const caseHistory = isSupremeCourtSource
    ? (caseData?.case?.historyCaseData?.length ? caseData.case.historyCaseData
      : caseData?.case?.caseHistory?.length ? caseData.case.caseHistory
      : scListingDates)
    : (caseData?.case?.caseHistory || caseData?.case?.historyData || []);
  const caseStatus = caseData?.case?.caseStatus || [];
  const documentsInfo = isSupremeCourtSource
    ? (caseData?.case?.transferDocs || [])
    : (caseData?.case?.documents || []);
  const objectionDetails = caseData?.case?.objectionDetails || [];
  // const invoiceInfo = caseData?.case?.invoiceInfo || [];
  const [allLawyers, setAllLawyers] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState("");
  const [assignedTo, setAssignedTo] = useState([]);
  const [editNote, setEditNote] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [editLabel, setEditLabel] = useState(null);
  const restrictedTabsForCustomer = ["Expenses", "Notes", "Tasks"];
  const { bubbleSettings } = useBubbleSettings();

  // Background upload state - persists across tab switches
  const [backgroundUploads, setBackgroundUploads] = useState([]);
  const [uploadPromisesRef] = useState({ current: {} });

  // Tab information content
  const tabInfoContent = {
    Orders: "This section displays all the court orders linked to the case. Each order includes details such as the judge's name, case number, and the date of the order. You can also access and view the official PDF of the order for reference and record-keeping.",
    Timeline: "The timeline helps track all the hearing dates in order. It allows you to add remarks or notes specific to each hearing for better follow-up. If no date is available, the system clearly indicates that as well.",
    'Document Details': "Here you will find all documents officially filed in court. It shows details like the advocate's name, document type (e.g., Vakalatnama, Petition), and the date the document was received. This ensures easy tracking of case-related paperwork.",
    'Objection Details': "This section lists any objections raised during the scrutiny of the case, along with dates for scrutiny, compliance, and receipt. This helps in tracking the status of objections.",
    'Request Docs': "This section is designed for client document management. You can request clients to upload specific case documents, upload them yourself, or review documents they have already provided. It helps keep all related files organized in one place.",
    Expenses: "The expenses tab is used to track and manage all case-related costs. You can record fees, filing charges, or any other expenses linked to the case. This makes it easy to monitor and calculate total spending.",
    Notes: "This is a simple space for maintaining important observations or remarks about the case. Notes can be added anytime to capture key points or strategies. It ensures important thoughts are never lost and can be referred back later.",
    Tasks: "The tasks section allows you to create and assign case-related work. Each task can be tracked for progress to ensure deadlines are met. This helps in managing workflows efficiently.",
    // Labels: "Labels are used to categorize and tag cases for easier identification. For example, you can mark cases as \"Urgent,\" \"Pending,\" or \"Closed.\" This helps in quick filtering and organization of multiple cases.",
    'My Doc': "My Doc works as your personal document storage system. You can create folders and subfolders to organize documents neatly. It supports uploads in formats like PDF, DOC, TXT, and images (up to 50MB), making it a flexible storage solution.",
    'Custom Columns': "This section allows you to view and manage additional custom data fields specific to this case. Depending on your role, you may be able to edit these fields to capture extra information relevant to your workflow."
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getLawyers();
        setAllLawyers(res.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCaseSettings = async () => {
      try {
        const res = await getAllGlobalSettingsData();
        if (res?.data?.result?.[0]?.cases) {
          setCaseSettings(res.data.result[0].cases);
        }
      } catch (err) {
        console.error("Error fetching case settings:", err);
      }
    };
    fetchCaseSettings();
  }, []);

  useEffect(() => {
    if (caseData?.case?.viewDetails?.courtRef) {
      getExpensesByCourtRef(caseData?.case?.viewDetails?.courtRef)
        .then((res) => setExpensesInfo(res.data))
        .catch((err) => console.error("Failed to fetch expenses:", err));
      getNotesByCourtRef(caseData?.case?.viewDetails?.courtRef)
        .then((res) => setNotesInfo(res.data))
        .catch((err) => console.error("Failed to fetch notes:", err));
      getTasksByCourtRef(caseData?.case?.viewDetails?.courtRef)
        .then((res) => setTasksInfo(res.data))
        .catch((err) => console.error("Failed to fetch tasks:", err));
      getAddedDocs(caseData?.case?.viewDetails?.courtRef)
        .then((res) => setAdditionalDocsInfo(res.data))
        .catch((err) => console.error("Failed to fetch docs:", err));
      getLabelByCourtRef(caseData?.case?.viewDetails?.courtRef)
        .then((res) => setLabelsInfo(res.data))
        .catch((err) => console.error("Failed to fetch labels:", err));

      // Updated: Use getDirectoryTree instead of getMyDocs for directory structure
      getDirectoryTree(caseData?.case?.viewDetails?.courtRef)
        .then((res) => {
          // The API returns { data: { success: true, tree: [...], total: number } }
          // Extract the tree and total from the response
          const responseData = res.data || res; // Handle axios response wrapper
          setMyDocInfo({
            myDocuments: responseData.tree || [], // Use tree directly
            total: responseData.total || 0
          });
        })
        .catch((err) => {
          console.error("Failed to fetch directory tree:", err);
          // Fallback to empty structure if directory tree fails
          setMyDocInfo({ myDocuments: [], total: 0 });
        });
    }
  }, [caseData?.case?.viewDetails?.courtRef]);


  const handleDeleteNote = async (id) => {
    try {
      await deleteNote(id);
      // Refresh notes from the server after deletion
      const refreshedNotes = await getNotesByCourtRef(caseData?.case?.viewDetails?.courtRef);
      setNotesInfo(refreshedNotes.data);
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await deleteExpense(id);
      // Refresh expenses from the server after deletion
      const refreshedExpenses = await getExpensesByCourtRef(caseData?.case?.viewDetails?.courtRef);
      setExpensesInfo(refreshedExpenses.data);
    } catch (err) {
      console.error("Error deleting expense:", err);
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await deleteTask(id);
      // Refresh tasks from the server after deletion
      const refreshedTasks = await getTasksByCourtRef(caseData?.case?.viewDetails?.courtRef);
      setTasksInfo(refreshedTasks.data);
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };
  const handleDeleteLabel = async (id) => {
    try {
      await deleteLabel(id);
      const refreshedLabels = await getLabelByCourtRef(caseData?.case?.viewDetails?.courtRef);
      setLabelsInfo(refreshedLabels.data);
    } catch (err) {
      console.error("Error deleting label:", err);
    }
  };
  useEffect(() => {
    // Helper function to get current date and time
    const getCurrentDateTime = () => {
      const now = new Date();
      const date = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      const time = now.toTimeString().slice(0, 5); // HH:MM format
      return { date, time };
    };


    if (showModal && modalTab === "Notes") {
      if (editNote) {
        setFormData({
          courtRef: editNote.courtRef || "",
          title: editNote.title || "",
          content: editNote.content || "",
          _id: editNote?._id,
        });
      } else {
        setFormData({
          courtRef: caseData?.case?.viewDetails?.courtRef || "",
          title: "",
          content: "",
        });
      }
    }
    else if (showModal && modalTab === "Expenses") {
      if (editExpense) {
        setFormData({
          type: "",
          date: editExpense.date || "",
          amount: editExpense.amount || "",
          description: editExpense.description || "",
          typeOfResource: "",
          resourceName: editExpense.resourceName || "",
          courtRef: caseData?.case?.viewDetails?.courtRef || "",
          document: editExpense.document || "",
          _id: editExpense?._id,
        });
      } else {
        setFormData({
          type: "",
          date: "",
          amount: "",
          description: "",
          typeOfResource: "",
          resourceName: "",
          courtRef: caseData?.case?.viewDetails?.courtRef || "",
          document: "",
        });
      }
    }
    else if (showModal && modalTab === "Tasks") {
      if (editTask) {
        setFormData({
          taskName: editTask.taskName || "",
          associatedCase: editTask.associatedCase || "",
          courtRef: caseData?.case?.viewDetails?.courtRef || "",
          assignedTo: editTask.assignedTo || assignedTo,
          description: editTask.description || "",
          dueDate: editTask.dueDate || "",
          document: editTask.document || "",
          recurringTask: {
            times: editTask.times || "",
            in: editTask.in || "",
          },
          _id: editTask?._id,
        });
      } else {
        // For new tasks in modal, set default date and time
        const { date, time } = getCurrentDateTime();
        setFormData({
          taskName: "",
          associatedCase: "",
          courtRef: caseData?.case?.viewDetails?.courtRef || "",
          assignedTo: assignedTo,
          description: "",
          dueDate: date, // Set default date
          dueTime: time, // Add default time
          document: "",
          recurringTask: {
            times: "",
            in: "",
          },
        });
      }
    }
    // else if (showModal && modalTab === "Labels") {
    //   if (editLabel) {
    //     setFormData({
    //       name: editLabel.name || "",
    //       description: editLabel.description || "",
    //       priorityLevel: editLabel.priorityLevel || "",
    //       branchId: editLabel.branchId || "",
    //       color: editLabel.color || "#3B96CB",
    //       highCourtCases: editLabel.highCourtCases || [],
    //       districtCourtCases: editLabel.districtCourtCases || [],
    //       otherCourtCases: editLabel.otherCourtCases || [],
    //       _id: editLabel?._id,
    //     });
    //   } else {
    //     setFormData({
    //       name: "",
    //       description: "",
    //       priorityLevel: "",
    //       branchId: "",
    //       color: "#3B96CB",
    //       highCourtCases: [],
    //       districtCourtCases: [],
    //       otherCourtCases: [],
    //     });
    //   }
    // }
  }, [showModal, modalTab, editNote, editExpense, editTask, caseData]);

  const isCustomColumnsEnabled = caseSettings?.allowCustomColumns;
  const shouldShowCustomColumnsTab = (() => {
    if (!isCustomColumnsEnabled) return false;

    // Show tab only if placement is 'tab-viewer'
    // (If placement is 'case-detail', it should show in case detail grid instead)
    return caseSettings?.placement === 'tab-viewer';
  })();

  // Customer role gets read-only access, all other roles can edit
  const isCustomColumnsReadOnly = role === 'customer';

  const baseTabDataMap = {
    Orders: orderInfo,
    Timeline: { data: caseHistory, status: caseStatus },
    ...(documentsInfo?.length > 0 ? { 'Document Details': documentsInfo } : {}),
    'Objection Details': objectionDetails,
    ...(shouldShowCustomColumnsTab ? { 'Custom Columns': [] } : {}),
    'Request Docs': AdditionalDocsInfo,
  };

  // Check if custom columns should be displayed in tab viewer


  const extendedTabDataMap = {
    ...baseTabDataMap,
    Expenses: expensesInfo,
    Notes: notesInfo,
    Tasks: tasksInfo,
    // Labels: labelsInfo,
    'My Doc': MyDocInfo
  };

  const tabDataMap = role === "customer" ? baseTabDataMap : extendedTabDataMap;

  const renderOrders = (orders) => {
    return <RenderOrders orders={orders} courtRef={caseData?.case?.viewDetails?.courtRef} isSupremeCourt={isSupremeCourtSource} />
  }

  const renderTimeline = (timeline) => {
    const source = caseData?.source?.toLowerCase() || '';
    const isSupremeCourt = source.includes('supreme') || selectedCourtType === 'SupremeCourt';
    return <RenderTimeline
      timeline={timeline}
      courtref={caseData?.case?.viewDetails?.courtRef}
      isSupremeCourt={isSupremeCourt}
    />
  }

  const renderDocuments = (documents) => {
    return <RenderDocuments documents={documents} variant={isSupremeCourtSource ? 'sc-other-docs' : 'default'} />
  }

  const renderObjection = (objections) => {
    return <RenderObjectionDetails objectionDetails={objections} />
  }


  const renderAdditionalDocuments = (additonaldocs) => {
    const refreshDocs = async () => {
      try {
        const res = await getAddedDocs(caseData?.case?.viewDetails?.courtRef);
        setAdditionalDocsInfo(res.data);
      } catch (err) {
        console.error("Failed to refresh additional docs:", err);
      }
    };

    return (
      <RenderAdditionalDocs
        additonaldocs={additonaldocs}
        selectedCourtType={selectedCourtType}
        courtRef={caseData?.case?.viewDetails?.courtRef}
        refreshDocs={refreshDocs}
      />
    );
  };

  const renderMyDocuments = (mydocs) => {
    const refreshDocs = async () => {
      try {
        // Use getDirectoryTree instead of getMyDocs for refresh
        const res = await getDirectoryTree(caseData?.case?.viewDetails?.courtRef);

        // Handle the response format properly
        const responseData = res.data || res; // Handle axios response wrapper
        setMyDocInfo({
          myDocuments: responseData.tree || [],
          total: responseData.total || 0
        });
      } catch (err) {
        console.error("Failed to refresh directory tree:", err);
        // Set empty structure on error
        setMyDocInfo({ myDocuments: [], total: 0 });
      }
    };

    return (
      <RenderMyDocs
        mydocs={mydocs}
        selectedCourtType={selectedCourtType}
        courtRef={caseData?.case?.viewDetails?.courtRef}
        refreshDocs={refreshDocs}
        backgroundUploads={backgroundUploads}
        setBackgroundUploads={setBackgroundUploads}
        uploadPromisesRef={uploadPromisesRef}
        removeBackgroundUpload={removeBackgroundUpload}
        clearCompletedUploads={clearCompletedUploads}
      />
    );
  };

  const renderNotes = (notes) => {
    return (
      <RenderNotes
        notes={notes}
        setModalTab={setModalTab}
        setShowModal={setShowModal}
        onEditNote={setEditNote}
        onDeleteNote={handleDeleteNote}
      />
    );
  };

  const renderExpenses = (expensesData) => {
    return <RenderExpenses
      expensesData={expensesData}
      setModalTab={setModalTab}
      setShowModal={setShowModal}
      onEditExpense={setEditExpense}
      onDeleteExpense={handleDeleteExpense}
    />
  };
  const renderLabels = (labelData) => {
    const refreshLabels = async () => {
      try {
        const refreshedLabels = await getLabelByCourtRef(caseData?.case?.viewDetails?.courtRef);
        setLabelsInfo(refreshedLabels.data);
      } catch (err) {
        console.error("Failed to refresh labels:", err);
      }
    };

    return <RenderLabels
      labelsData={labelData}
      setModalTab={setModalTab}
      setShowModal={setShowModal}
      onEditLabel={setEditLabel}
      onDeleteLabel={handleDeleteLabel}
      courtRef={caseData?.case?.viewDetails?.courtRef}
      refreshLabels={refreshLabels}
      refreshCaseDetailLabels={refreshCaseDetailLabels} // Add this prop
    />
  };

  const renderTasks = (tasks) => {
    return <RenderTasks
      tasks={tasks}
      setModalTab={setModalTab}
      setShowModal={setShowModal}
      onEditTask={setEditTask}
      onDeleteTask={handleDeleteTask}
    />
  }

  const renderCustomColumns = () => {
    const source = caseData?.source?.toLowerCase() || '';
    const type = selectedCaseType?.toLowerCase() || 'fetched';

    let caseType = 'oc-added'; // Default

    if (source.includes('high')) {
      caseType = type === 'added' ? 'hc-added' : 'hc-fetched';
    } else if (source.includes('district')) {
      caseType = type === 'added' ? 'dc-added' : 'dc-fetched';
    } else if (source.includes('other')) {
      caseType = type === 'added' ? 'oc-added' : 'oc-fetched';
    } else if (source.includes('supreme')) {
      caseType = type === 'added' ? 'sc-added' : 'sc-fetched';
    } else {
      // Fallback based on selectedCourtType
      if (selectedCourtType === 'HighCourt') {
        caseType = type === 'added' ? 'hc-added' : 'hc-fetched';
      } else if (selectedCourtType === 'DistrictCourt') {
        caseType = type === 'added' ? 'dc-added' : 'dc-fetched';
      } else if (selectedCourtType === 'SupremeCourt') {
        caseType = type === 'added' ? 'sc-added' : 'sc-fetched';
      } else {
        caseType = type === 'added' ? 'oc-added' : 'oc-fetched';
      }
    }

    return (
      <RenderCustomColumns
        caseSettings={caseSettings}
        courtRef={caseData?.case?.viewDetails?.courtRef}
        caseType={caseType}
        existingData={caseData?.case?.customAttributes}
        isReadOnly={isCustomColumnsReadOnly}

      />
    );
  };

  const renderSimpleList = (items, label) => (
    <div className="tab-content">
      {items.length === 0 ? (
        <div className="no-data-container">
          <div className="no-data-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="no-data-title">No {label} Found</h3>
          <p className="no-data-message">
            There are currently no {label.toLowerCase()} available for this case.
            Check back later or contact support if you think this is an error.
          </p>
        </div>
      ) : (
        items.map((item, i) => (
          <div key={i} className="simple-item">
            <pre>{JSON.stringify(item, null, 2)}</pre>
          </div>
        ))
      )}
    </div>
  );

  const courtRef = caseData?.case?.viewDetails?.courtRef;
  const bubbleCases = [
    ...(bubbleSettings?.case?.highCourt || []),
    ...(bubbleSettings?.case?.districtCourt || [])
  ];
  const matchingBubble = bubbleCases.find(entry => entry.courtRef === courtRef);
  const getTabBubbleCount = (tab) => {
    if (!matchingBubble) return 0;
    if (tab === 'Tasks') return matchingBubble.taskCount || 0;
    if (tab === 'Additional Doc') return matchingBubble.documentCount || 0;

    return 0; // for all other tabs
  };

  // Helper functions for background uploads
  const removeBackgroundUpload = (uploadId) => {
    setBackgroundUploads(prev => prev.filter(u => u.id !== uploadId));
  };

  const clearCompletedUploads = () => {
    setBackgroundUploads(prev => prev.filter(u => u.status === 'uploading'));
  };

  const activeUploadsCount = backgroundUploads.filter(u => u.status === 'uploading').length;

  return (
    <div className="tab-container">
      {/* Global Upload Notification Banner - Shows across all tabs */}
      {/* {backgroundUploads.length > 0 && (
        <div className="global-upload-notification">
          <div className="upload-banner">
            <div className="upload-banner-left">
              <span className="upload-icon">📤</span>
              <span className="upload-text">
                {activeUploadsCount > 0 
                  ? `Uploading ${activeUploadsCount} file(s) in My Doc...` 
                  : 'Uploads completed'}
              </span>
            </div>
            <div className="upload-banner-right">
              {backgroundUploads.some(u => u.status !== 'uploading') && (
                <button 
                  className="clear-completed-btn"
                  onClick={clearCompletedUploads}
                  title="Clear completed/failed"
                >
                  Clear
                </button>
              )}
              <button 
                className="view-uploads-btn"
                onClick={() => setActiveTab('My Doc')}
                title="View uploads"
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      )} */}

      <div className="tab-buttons-with-info">
        <div className="tab-buttons">
          {Object.keys(tabDataMap).map((tab) => {
            const count = getTabBubbleCount(tab);

            return (
              <button
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
                {count > 0 && <span className="tab-bubble">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Info Button */}
        <div className="tab-info-container">
          <button className="info-button">
            i
            <div className="info-tooltip">
              {tabInfoContent[activeTab] || "Information not available for this tab."}
            </div>
          </button>
        </div>
      </div>

      {(activeTab === "Orders")
        ? renderOrders(tabDataMap[activeTab])
        : (activeTab === "Timeline")
          ? renderTimeline(tabDataMap[activeTab])
          : (activeTab === "Document Details")
            ? renderDocuments(tabDataMap[activeTab])
            : (activeTab === "Objection Details")
              ? renderObjection(tabDataMap[activeTab])
              : (activeTab === "Expenses" && role !== "customer")
                ? renderExpenses(tabDataMap[activeTab])
                // : (activeTab === "Labels" && role !== "customer")
                //   ? renderLabels(tabDataMap[activeTab])
                : (activeTab === "Notes" && role !== "customer")
                  ? renderNotes(tabDataMap[activeTab])
                  : (activeTab === "Tasks" && role !== "customer")
                    ? renderTasks(tabDataMap[activeTab])
                    : (activeTab === "Custom Columns" && shouldShowCustomColumnsTab)
                      ? renderCustomColumns()
                      : (activeTab === "Request Docs")
                        ? renderAdditionalDocuments(tabDataMap[activeTab], selectedCourtType)
                        : (activeTab === "My Doc")
                          ? renderMyDocuments(tabDataMap[activeTab], selectedCourtType)
                          : renderSimpleList(tabDataMap[activeTab], activeTab)
      }

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            {modalTab === "Notes" && (
              <NoteForm
                initialData={editNote || { courtRef: caseData?.case?.viewDetails?.courtRef }}
                onSuccess={async (courtRef) => {
                  const refreshedNotes = await getNotesByCourtRef(courtRef);
                  setNotesInfo(refreshedNotes.data);
                  setEditNote(null);
                  setShowModal(false);
                }}
                onCancel={() => {
                  setEditNote(null);
                  setShowModal(false);
                }}
                disabledCourtRef={true}
                isModal={true}
              />
            )}

            {modalTab === "Expenses" && (
              <>
                <ExpenseForm
                  initialData={editExpense || { courtRef: caseData?.case?.viewDetails?.courtRef }}
                  onSuccess={async (courtRef) => {
                    const refreshedExpense = await getExpensesByCourtRef(courtRef);
                    setExpensesInfo(refreshedExpense.data);
                    setEditExpense(null);
                    setShowModal(false);
                  }}
                  onCancel={() => {
                    setEditExpense(null);
                    setShowModal(false);
                  }}
                  disabledCourtRef={true}
                  isModal={true}
                />
              </>
            )}
            {modalTab === "Labels" && (
              <LabelForm
                initialData={editLabel || { courtRef: caseData?.case?.viewDetails?.courtRef }}
                onSuccess={async () => {
                  // Refresh labels in the current view
                  const refreshedLabels = await getLabelByCourtRef(caseData?.case?.viewDetails?.courtRef);
                  setLabelsInfo(refreshedLabels.data);

                  // Also refresh the case detail labels if the function is available
                  if (refreshCaseDetailLabels) {
                    await refreshCaseDetailLabels();
                  }

                  setEditLabel(null);
                  setShowModal(false);
                }}
                onCancel={() => {
                  setEditLabel(null);
                  setShowModal(false);
                }}
                isModal={true}
              />
            )}

            {modalTab === "Tasks" && (
              <>
                <TaskForm
                  initialData={editTask || { courtRef: caseData?.case?.viewDetails?.courtRef }}
                  onSuccess={async (courtRef) => {
                    const refreshedTasks = await getTasksByCourtRef(courtRef);
                    setTasksInfo(refreshedTasks.data);
                    setEditTask(null);
                    setShowModal(false);
                  }}
                  onCancel={() => {
                    setEditTask(null);
                    setShowModal(false);
                  }}
                  disabledCourtRef={true}
                  isModal={true}
                />
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default TabViewer;